import React, { useState } from 'react';
import { pmTheme } from './pmTheme';

interface TimeTrackerSettingsProps {
  onClose: () => void;
  defaultBillable: boolean;
  timeRounding: number;
  currency: string;
  accentColor?: string;
  onSave: (settings: {
    defaultBillable: boolean;
    timeRounding: number;
    currency: string;
  }) => void;
}

const ROUNDING_OPTIONS = [
  { value: 1, label: '1 minute' },
  { value: 5, label: '5 minutes' },
  { value: 6, label: '6 minutes (1/10 hr)' },
  { value: 10, label: '10 minutes' },
  { value: 15, label: '15 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 60, label: '1 hour' },
];

const CURRENCIES = [
  { code: 'USD', label: 'US Dollar ($)' },
  { code: 'EUR', label: 'Euro (\u20AC)' },
  { code: 'GBP', label: 'British Pound (\u00A3)' },
  { code: 'CAD', label: 'Canadian Dollar (C$)' },
  { code: 'AUD', label: 'Australian Dollar (A$)' },
  { code: 'JPY', label: 'Japanese Yen (\u00A5)' },
  { code: 'CHF', label: 'Swiss Franc (CHF)' },
  { code: 'NZD', label: 'New Zealand Dollar (NZ$)' },
];

export const TimeTrackerSettings: React.FC<TimeTrackerSettingsProps> = ({
  onClose,
  defaultBillable,
  timeRounding,
  currency,
  accentColor = '#10b981',
  onSave,
}) => {
  const [localBillable, setLocalBillable] = useState(defaultBillable);
  const [localRounding, setLocalRounding] = useState(timeRounding);
  const [localCurrency, setLocalCurrency] = useState(currency);

  const handleSave = () => {
    onSave({
      defaultBillable: localBillable,
      timeRounding: localRounding,
      currency: localCurrency,
    });
    onClose();
  };

  const inputStyle: React.CSSProperties = {
    padding: '8px 12px', border: `1px solid ${pmTheme.input}`, borderRadius: 6,
    fontSize: 13, outline: 'none', width: '100%', color: pmTheme.text, background: pmTheme.surface,
  };

  return (
    <div
      data-testid="time-tracker-settings"
      style={{
        position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: pmTheme.surface, color: pmTheme.text, borderRadius: 12, width: '90%', maxWidth: 440,
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        boxShadow: 'var(--shadow-xl)',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: `1px solid ${pmTheme.border}` }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: pmTheme.text }}>Time Tracker Settings</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, color: pmTheme.textSubtle, cursor: 'pointer', padding: 0 }}>×</button>
        </div>

        {/* Settings form */}
        <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Default Billable */}
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={localBillable}
                onChange={(e) => setLocalBillable(e.target.checked)}
                style={{ width: 18, height: 18, accentColor }}
              />
              <div>
                <div style={{ fontSize: 14, fontWeight: 500, color: pmTheme.text }}>Default to billable</div>
                <div style={{ fontSize: 12, color: pmTheme.textSubtle }}>New time entries will be marked as billable by default</div>
              </div>
            </label>
          </div>

          {/* Time Rounding */}
          <div>
            <label style={{ fontSize: 14, fontWeight: 500, color: pmTheme.text, display: 'block', marginBottom: 6 }}>
              Time rounding
            </label>
            <select value={localRounding} onChange={(e) => setLocalRounding(Number(e.target.value))} style={inputStyle}>
              {ROUNDING_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <div style={{ fontSize: 12, color: pmTheme.textSubtle, marginTop: 4 }}>Duration will be rounded up to the nearest increment</div>
          </div>

          {/* Currency */}
          <div>
            <label style={{ fontSize: 14, fontWeight: 500, color: pmTheme.text, display: 'block', marginBottom: 6 }}>
              Currency
            </label>
            <select value={localCurrency} onChange={(e) => setLocalCurrency(e.target.value)} style={inputStyle}>
              {CURRENCIES.map((c) => (
                <option key={c.code} value={c.code}>{c.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 20px', borderTop: `1px solid ${pmTheme.border}` }}>
          <button onClick={onClose} style={{
            padding: '8px 16px', border: `1px solid ${pmTheme.input}`, borderRadius: 6,
            background: pmTheme.surface, color: pmTheme.text, fontSize: 13, cursor: 'pointer',
          }}>
            Cancel
          </button>
          <button onClick={handleSave} style={{
            padding: '8px 16px', border: 'none', borderRadius: 6,
            background: accentColor, color: pmTheme.textInverse, fontSize: 13, fontWeight: 500, cursor: 'pointer',
          }}>
            Save Settings
          </button>
        </div>
      </div>
    </div>
  );
};

TimeTrackerSettings.displayName = 'TimeTrackerSettings';
